// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentsList.test.tsx
//
// The pieces one findings card is drawn from, kept apart from the card's own
// state (selection, judgment, edit) in `./AskV2ReviewCommentCard.tsx`.
import { ArrowsMerge, CaretUp } from "@phosphor-icons/react";
import { Badge, Text } from "@radix-ui/themes";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { type CommentWithOptionalTags } from "./reviewCommentTags";

import {
  AskPunttReviewCommentBadge,
  commentSectionLabelKey,
  formatReviewCommentTime,
  reviewCommentCollapsedSections,
  reviewCommentSpecQuote,
} from "../../components/ask-puntt-review";
import {
  KNOWLEDGE_DATABASE_DEFAULT_SEARCH,
  KNOWLEDGE_DATABASE_PATH,
} from "../../routes/settings/routes/knowledge-database/search";

export function CommentSeverityBadge({
  severity,
}: {
  severity: CommentWithOptionalTags["severity"];
}) {
  const { t } = useTranslation("ui");
  if (!severity) return null;

  const label =
    severity === "high"
      ? t("highConfidence")
      : severity === "medium"
        ? t("mediumConfidence")
        : t("lowConfidence");

  return (
    <Badge
      size="1"
      variant="soft"
      color="gray"
      className="inline-flex items-center gap-1"
      data-testid="ask-v2-review-comment-confidence-badge"
    >
      <CaretUp size={10} weight="bold" aria-hidden="true" />
      {label}
    </Badge>
  );
}

// Room for N size-1 icon buttons (24px) with gap-2 between them.
const ACTIONS_RESERVE_CLASS: Readonly<Record<number, string>> = {
  1: "pr-8",
  2: "pr-16",
};

export function CommentIdentityRow({
  id,
  commentNumber,
  tone,
  authorLabel,
  createdAt,
  edited = false,
  actionCount = 0,
}: {
  id: string;
  commentNumber: number;
  tone: "strong" | "soft";
  authorLabel: string;
  createdAt: string;
  /** The author changed the text after posting it. */
  edited?: boolean;
  /** Header icons drawn over the row's right edge; their room is reserved. */
  actionCount?: number;
}) {
  const { t } = useTranslation("ui");
  const reserve = ACTIONS_RESERVE_CLASS[actionCount];
  return (
    <div
      className={`flex min-w-0 items-center gap-3${reserve ? ` ${reserve}` : ""}`}
      data-testid={`ask-v2-review-comment-identity-${id}`}
    >
      <AskPunttReviewCommentBadge
        number={commentNumber}
        tone={tone}
        data-testid={`ask-v2-review-comment-index-${id}`}
      />
      <div
        className="flex min-w-0 items-center gap-2 text-[12px] text-puntt-neutral-gray-9"
        data-testid={`ask-v2-review-comment-author-${id}`}
      >
        <span className="truncate font-medium text-puntt-neutral-gray-11">
          {authorLabel}
        </span>
        <span className="shrink-0">{formatReviewCommentTime(createdAt)}</span>
        {edited ? (
          <span
            className="shrink-0"
            data-testid={`ask-v2-review-comment-edited-${id}`}
          >
            {t("askV2.review.commentEdited")}
          </span>
        ) : null}
      </div>
    </div>
  );
}

export function SpecQuoteLine({
  comment,
}: {
  comment: CommentWithOptionalTags;
}) {
  const { t } = useTranslation("ui");
  const quote = reviewCommentSpecQuote(comment);
  if (!quote) return null;
  return (
    <Text
      as="p"
      size="2"
      className="mt-2 line-clamp-2 text-puntt-neutral-gray-10"
      data-testid={`ask-v2-review-comment-spec-${comment._id}`}
    >
      <span className="font-semibold text-puntt-neutral-gray-11">
        {t("askV2.review.specLabel")}:
      </span>{" "}
      {quote}
    </Text>
  );
}

export function CollapsedCommentPreview({
  comment,
}: {
  comment: CommentWithOptionalTags;
}) {
  const { t } = useTranslation("ui");
  const sections = reviewCommentCollapsedSections(comment.description);
  if (sections.length === 0) {
    const preview = comment.description.trim().split("\n").slice(1).join(" ");
    if (!preview) return null;
    return (
      <Text as="p" size="3" className="mt-2 text-puntt-neutral-gray-10">
        {preview}
      </Text>
    );
  }

  return (
    <div
      className="mt-2 space-y-2"
      data-testid={`ask-v2-review-comment-preview-${comment._id}`}
    >
      {sections.map((section) => {
        const labelKey = commentSectionLabelKey(section.label);
        const heading = labelKey == null ? section.label : t(labelKey);
        return (
          <Text
            as="p"
            key={section.label}
            size="3"
            className="text-puntt-neutral-gray-11"
            data-testid={`ask-v2-review-comment-section-${comment._id}-${section.label.toLowerCase()}`}
          >
            <span className="font-semibold text-puntt-neutral-gray-12">
              {heading}:
            </span>{" "}
            {section.body}
          </Text>
        );
      })}
    </div>
  );
}

/**
 * A finding she judged while the review was still running, which warhol's own
 * dedup then folded into another finding when the turn ended.
 *
 * Kept rather than deleted — the judgment is hers — and labelled, so the list
 * does not read as two comments for one defect. Only ever set on a comment
 * that carries a judgment: an unjudged provisional finding is removed outright
 * (see `mergeWarholReviewComments`).
 */
export function FoldedIntoSubheading({
  comment,
  commentId,
}: {
  comment: CommentWithOptionalTags;
  commentId: string;
}) {
  const { t } = useTranslation("ui");
  const folded = comment.foldedInto;
  if (!folded) return null;
  return (
    <div
      className="mt-2 rounded-md border border-puntt-neutral-gray-5 bg-puntt-neutral-gray-2 px-2.5 py-1.5"
      data-testid={`ask-v2-review-comment-folded-into-${commentId}`}
    >
      <Text
        as="p"
        size="2"
        weight="medium"
        className="flex items-center gap-1.5 text-puntt-neutral-gray-11"
      >
        <ArrowsMerge size={16} weight="bold" aria-hidden="true" />
        {t("askV2.review.foldedInto")}
      </Text>
      <Text
        as="p"
        size="1"
        color="gray"
        className="mt-0.5 line-clamp-3 whitespace-pre-line"
        data-testid={`ask-v2-review-comment-folded-into-text-${commentId}`}
      >
        {folded.description}
      </Text>
    </div>
  );
}

/**
 * Where a past decision is logged: the knowledge rule the finding cited when
 * there is one, else the review it was made on. Rendered in the open card's
 * body, not the header, because the header is a button and a link inside a
 * button is invalid markup that swallows keyboard focus.
 */
export function JudgmentReceiptLink({
  comment,
  commentId,
}: {
  comment: CommentWithOptionalTags;
  commentId: string;
}) {
  const { t } = useTranslation("ui");
  const receipt = comment.previouslyRejected ?? comment.previouslyConfirmed;
  if (!receipt) return null;
  const className =
    "mb-2 inline-block text-[13px] font-medium text-puntt-blue-11 underline";
  if (receipt.ruleId) {
    return (
      <Link
        to={KNOWLEDGE_DATABASE_PATH}
        search={{
          ...KNOWLEDGE_DATABASE_DEFAULT_SEARCH,
          tab: "rules",
          ruleId: receipt.ruleId,
        }}
        target="_blank"
        className={className}
        data-testid={`ask-v2-review-comment-judgment-rule-link-${commentId}`}
      >
        {t("assetViewer.viewJudgmentRule")}
      </Link>
    );
  }
  if (receipt.chatId && receipt.reviewId) {
    return (
      <Link
        // why: the typed route register names this path by its v1 param, `$ticketId`;
        // it is the same URL the v2 review route serves, so the link resolves there.
        to="/ask-puntt/$chatId/reviews/$ticketId"
        params={{ chatId: receipt.chatId, ticketId: receipt.reviewId }}
        target="_blank"
        className={className}
        data-testid={`ask-v2-review-comment-judgment-review-link-${commentId}`}
      >
        {t("assetViewer.viewJudgmentReview")}
      </Link>
    );
  }
  return null;
}

/** What she may do to a comment: judge an AI finding, edit or delete her own. */
export function reviewCommentPermissions({
  comment,
  currentUserId,
  onJudge,
  onEdit,
  onDelete,
}: {
  comment: CommentWithOptionalTags;
  currentUserId?: string;
  onJudge?: unknown;
  onEdit?: unknown;
  onDelete?: unknown;
}) {
  // why: read-only viewers get no onJudge, and only AI findings write the existing `liked` field
  const canJudge =
    onJudge != null && comment.isAI && !comment.isPending && !comment.isDeleted;
  // why: christo refuses a text edit or a delete from anyone but the author
  // of a human comment; offering either anywhere else would only end in a 403.
  const ownsComment =
    !comment.isAI &&
    !comment.isDeleted &&
    currentUserId != null &&
    comment.createdBy === currentUserId;
  return {
    canJudge,
    canEdit: onEdit != null && ownsComment,
    canDelete: onDelete != null && ownsComment,
  };
}
